import { getLocalizationById } from '@context/useMyRouting/helpers';
import getLocalizationByArgs from '@helpers/getLocalizationByArgs';
import { SelectOption } from '@pages/AdditionalServices/containers/services/optionsGenerator/types';
import { ecoFeeLoader } from '@services/requests/searchLoaders/ecoFeeLoader';
import { EcoFeePackagesLoaderResponse } from '@services/requests/searchLoaders/ecoFeeLoader/EcoFeePackagesLoaderQuery';
import { EcoFeeProductsLoaderResponse } from '@services/requests/searchLoaders/ecoFeeLoader/EcoFeeProductsLoaderQuery';
import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { v4 } from 'uuid';

/**
 * EcoFeeSearchContext описывает типизацию контекста поля поиска продуктов или упаковок экосбора
 */
export interface EcoFeeSearchContext {
  /**
   * Язык локализации системы. Нужен для вычисления опций.
   */
  LangID: string;

  /**
   * Найденные товары или упаковки экосбора после поиска.
   */
  LoadedOptions: { [T in string]: SelectOption[] };
}

const ecoFeeSearchContext$ = new BehaviorSubject<EcoFeeSearchContext>({
  LangID: '',
  LoadedOptions: {},
});

/**
 * UserEcoFeeSearch содержит текущий стейт результатов поиска для пользовательского
 * компонента. Он уникален для каждого компонента в отдельности и содержит именно
 * его данные.
 */
export interface UserEcoFeeSearch {
  /**
   * SearchString содержит строку поиска. При изменении запускается автоматический
   * поиск товаров или упаковок, на основе подписки.
   */
  SearchString: string;

  /**
   * Идентификатор последнего запроса на поиск. Именно он будет актуален.
   * Запросы могут проходить с разной скоростью и более ранние могут вернуться
   * позже, чем крайний, поэтому их необходимо будет отбросить, оставив только
   * результаты актуального.
   */
  LastSearchID: string;

  /**
   * Выбранная опция. Нужная для повторяющихся поисков после выбора опций.
   * Ее наличие отключает такие глюки.
   */
  SelectedOption?: SelectOption;

  /**
   * Загруженные опции поиска для конкретного компонента.
   */
  Options: SelectOption[];
}

/**
 * Обработчик изменения строки поиска. Записывает изменение в стейт, возвращает
 * найденные опции по запросу.
 * @param searchString
 */
const onSearchByString = async (
  searchString: string,
  groupType: 'packages' | 'products'
): Promise<SelectOption[]> => {
  if (searchString.length <= 0) {
    return [];
  }

  const state = ecoFeeSearchContext$.getValue();
  if (!!state.LoadedOptions[searchString]) {
    return state.LoadedOptions[searchString];
  }

  const service =
    groupType === 'packages'
      ? ecoFeeLoader().LoadPackagesByParams({
          where: { default_name: { _like: `%${searchString}%` } },
        })
      : ecoFeeLoader().LoadProductsByParams({
          where: {
            _or: [
              { tnved_code: { _like: `%${searchString}%` } },
              { okved_code: { _like: `%${searchString}%` } },
              { default_name: { _like: `%${searchString}%` } },
            ],
          },
        });

  const options = await service;

  const loadedOptions = mapOptions(options, state.LangID);
  const newState = ecoFeeSearchContext$.getValue();
  ecoFeeSearchContext$.next({
    ...newState,
    LoadedOptions: {
      ...newState.LoadedOptions,
      [searchString]: loadedOptions,
    },
  });

  return loadedOptions;
};

/**
 * mapOptions возвращает смапленные продукты или упаковки в опции селектора
 * @param options
 * @param langID
 */
const mapOptions = (
  options: EcoFeePackagesLoaderResponse['items'] | EcoFeeProductsLoaderResponse['items'],
  langID: string
): SelectOption[] => {
  const langCode = getLocalizationById(langID).countryCode;
  const t = i18next.getFixedT(langCode);

  return options
    .map(
      (
        d:
          | EcoFeePackagesLoaderResponse['items'][number]
          | EcoFeeProductsLoaderResponse['items'][number]
      ) => ({
        value: parseInt(d.id),
        label: `${
          d.group.group_type === 'products' ? t('Common:Product') : t('Common:Package')
        }: ${getLocalizationByArgs(langID, d.default_name ?? '', d.localized_names_data)}${
          d.group.group_type === 'products'
            ? `, [${t('Common:TNVED')}: ${
                (d as EcoFeeProductsLoaderResponse['items'][number]).tnved_code
              } / ${t('Common:OKVED')}: ${
                (d as EcoFeeProductsLoaderResponse['items'][number]).okved_code
              }]`
            : ''
        }`,
        description: `${t('Common:Group')} №${d.group.number_group} "${
          d.group.name ?? ''
        }" ${`/ ${t('Common:RateValue')}: ${
          (d as EcoFeeProductsLoaderResponse['items'][number]).group.rate_value ?? ''
        }  /  ${t('Common:Normative')}: ${
          (d as EcoFeeProductsLoaderResponse['items'][number]).group.normative_value ?? ''
        }  /  ${t('Common:Coefficient')}: ${
          (d as EcoFeeProductsLoaderResponse['items'][number]).group.coefficient ?? ''
        }`}`,
        origin: d,
      })
    )
    .filter((o) => !!o)
    .filter((d, i, data) => data.map((o) => o.value).indexOf(d.value) === i);
};

/**
 * Инициализация компонента и контекста. Содержит все подписки на поиск и т.д.
 * @param langID
 */
const init = (langID: string) => {
  ecoFeeSearchContext$.next({
    ...ecoFeeSearchContext$.getValue(),
    LangID: langID,
  });
};

/**
 * makeOptionLabel генерирует название опции для строки поиска по
 * переданной опции.
 * @param option
 */
const makeOptionLabel = (
  groupType: 'packages' | 'products',
  langID: string,
  option?: SelectOption
) => {
  if (!option) {
    return '';
  }

  const langCode = getLocalizationById(langID).countryCode;
  const t = i18next.getFixedT(langCode);

  if (groupType === 'products') {
    return [
      `${t('Common:Product')}: ${
        getLocalizationByArgs(
          langID,
          option.origin.default_name ?? '',
          option.origin.localized_names_data
        ) || ''
      }`,
      `${t('Common:Group')} №${option.origin.group.number_group} "${
        option.origin.group.name ?? ''
      }"`,
      `${t('Common:RateValue')}: ${option.origin.group.rate_value ?? ''}    ${t(
        'Common:Normative'
      )}: ${option.origin.group.normative_value ?? ''}    ${t('Common:Coefficient')}: ${
        option.origin.group.coefficient ?? ''
      }`,
      `${t('Common:TNVED')}: ${option.origin.tnved_code ?? ''} / ${t('Common:OKVED')}: ${
        option.origin.okved_code ?? ''
      }`,
    ].join('\n\n');
  }

  return option.label + ', ' + option.description;
};

/**
 * Стейты пользователей. По сути заранее заведенные стейты, которые
 * извлекаются по ID
 */
const userStates: { [T in string]: BehaviorSubject<UserEcoFeeSearch> } = {};
const getState = (id: string) => {
  if (!!userStates[id]) {
    return userStates[id];
  }

  userStates[id] = new BehaviorSubject<UserEcoFeeSearch>({
    LastSearchID: '',
    SearchString: '',
    SelectedOption: undefined,
    Options: [],
  });

  return userStates[id];
};

/**
 * useUserSearch реализует хук для конечного компонента поиска товаров или упаковок.
 * Позволяет задействовать индивидуальный стейт для поиска по товарам или упаковкам.
 * @param initialOptions
 * @param optionValue
 * @param selectedOption
 * @param groupType
 * @param langID
 */
const useUserSearch = (
  langID: string,
  optionValue: number,
  initialOptions: SelectOption[] = [],
  groupType: 'packages' | 'products',
  selectedOption?: SelectOption
) => {
  /**
   * Генерируем стейт для пользователя или подтягиваем имеющийся
   */
  const stateID = useMemo(() => v4(), []);
  const [state, setState] = useState(getState(stateID).getValue());
  useEffect(() => {
    const subscription = getState(stateID)
      .pipe()
      .subscribe({
        next: (state) => setState(state),
      });

    return () => {
      try {
        subscription.unsubscribe();
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    const data = getState(stateID).getValue();
    if (data.Options.length > 0) {
      return;
    }

    getState(stateID).next({
      ...data,
      Options: initialOptions,
    });
  }, [initialOptions]);

  useEffect(() => {
    const data = getState(stateID).getValue();
    if (data.SelectedOption?.value === selectedOption?.value) {
      return;
    }

    getState(stateID).next({
      ...data,
      SelectedOption: selectedOption,
      SearchString: makeOptionLabel(groupType, langID, selectedOption),
    });
  }, [selectedOption]);

  useEffect(() => {
    onInitializeOption(optionValue, groupType);
  }, []);

  /**
   * onInitializeOption позволяет инициировать загрузку опций компонента по
   * базовому идентификатору опции
   * @param id
   */
  const onInitializeOption = async (id: number, groupType: 'packages' | 'products') => {
    try {
      const service =
        groupType === 'packages'
          ? ecoFeeLoader().LoadPackagesByParams({ where: { id: { _equals: String(id) } } })
          : ecoFeeLoader().LoadProductsByParams({ where: { id: { _equals: String(id) } } });
      const options = mapOptions(await service, langID);
      const state = getState(stateID).getValue();

      if (state.Options.length !== 0) {
        return;
      }

      getState(stateID).next({
        ...state,
        SelectedOption: options.find((o) => o.value === id),
        Options: options,
        SearchString: makeOptionLabel(groupType, langID, selectedOption),
      });
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Шина поиска товаров или упаковок с тротлингом. Тротлит пол секунды до поиска и ищет
   * товары или упаковки, когда есть изменения в отправленных запросах.
   */
  const [bus] = useState(new Subject<string[]>());
  useEffect(() => {
    const subscribe = bus.pipe(debounceTime(500), distinctUntilChanged()).subscribe({
      next: async (data) => {
        const [searchString, lastSearchID] = data;
        if (0 === searchString.length) {
          return;
        }

        try {
          const options = await onSearchByString(searchString, groupType);

          const data = getState(stateID).getValue();
          if (data.LastSearchID !== lastSearchID) {
            return;
          }

          getState(stateID).next({
            ...data,
            LastSearchID: '',
            Options: options,
          });
        } catch (e) {
          console.error(e);
        } finally {
          const data = getState(stateID).getValue();
          if (data.LastSearchID === lastSearchID) {
            getState(stateID).next({
              ...data,
              LastSearchID: '',
            });
          }
        }
      },
    });

    return () => {
      try {
        subscribe.unsubscribe();
      } catch (e) {}
    };
  }, []);

  /**
   * onSelectOption сохраняет выбранную опцию в стейт.
   * @param option
   */
  const onSelectOption = (
    groupType: 'packages' | 'products',
    langID: string,
    option?: SelectOption
  ) => {
    getState(stateID).next({
      ...getState(stateID).getValue(),
      SelectedOption: option,
      SearchString: makeOptionLabel(groupType, langID, option),
    });
  };

  /**
   * onChangeSearch реализует callback для обработки изменения строки поиска и
   * загрузки товаров или упаковок для нее.
   * @param searchString
   */
  const onChangeSearch = (
    groupType: 'packages' | 'products',
    searchString: string,
    langID: string
  ) => {
    const data = getState(stateID).getValue();
    const currentLabel = makeOptionLabel(groupType, langID, data.SelectedOption);
    if (searchString == currentLabel && searchString.length !== 0) {
      return;
    }

    const lastSearchID = v4();
    getState(stateID).next({
      ...data,
      LastSearchID: 0 === searchString.length ? '' : lastSearchID,
      SearchString: searchString,
    });

    bus.next([searchString, lastSearchID]);
  };

  return {
    state,
    makeOptionLabel,
    onChangeSearch,
    onSelectOption,
  };
};

/**
 * Хук для подключения к компоненту. Автоматически выгружает текущий стейт,
 * а так же прокидывает callback изменений.
 */
const useEcoFeeSearchContext = () => {
  return {
    init,
    useUserSearch,
  };
};

export default useEcoFeeSearchContext;
